<template>
  <div class="password-recovery">
    <el-row>
      <h2 class="text__main-title text--bold append-dot mb-16">{{ $t('enter_new_password') }}</h2>
      <p v-if="backendError" class="text--centered text--error">
        {{ backendError }}
      </p>
      <ElForm
        class="password-recovery__form"
        :model="form"
        :rules="rules"
        ref="form"
        label-position="top"
        hide-required-asterisk
        @submit.native.prevent="submitForm"
        v-if="userData"
      >
        <ElFormItem prop="password">
          <ElRow slot="label" class="login__password_label" type="flex" justify="space-between">
            <span>{{ $t('password_label') }}</span>
          </ElRow>
          <ElInput
            id="password"
            type="password"
            v-model="form.password"
            :placeholder="$t('password_placeholder')"
            show-password
          />
        </ElFormItem>

        <ElFormItem class="mb-24" prop="confirmPassword">
          <ElRow slot="label" class="login__password_label" type="flex" justify="space-between">
            <span>{{ $t('confirm_password_label') }}</span>
          </ElRow>
          <ElInput
            id="passwordConfirm"
            v-model="form.confirmPassword"
            :placeholder="$t('password_placeholder')"
            show-password
          />
        </ElFormItem>
        <ElFormItem>
          <ElRow type="flex" justify="center">
            <ElButton
              id="submit"
              type="primary"
              class="enter-button"
              native-type="submit"
              :loading="pageIsLoading"
            >{{ $t('change_password') }}
            </ElButton
            >
          </ElRow>
        </ElFormItem>
      </ElForm>
    </el-row>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
// Helpers
import entranceHttpService from '@/resource/EntranceHttpService';
import i18n from '@/plugins/i18n';
// Types
import { ElForm } from 'element-ui/types/form';

@Component
export default class ChangePassword extends Vue {
  $refs!: {
    form: ElForm;
  };

  private form = {
    token: '',
    password: '',
    confirmPassword: '',
  };

  private pageIsLoading = false;
  private backendError: string = '';
  private mustChangePassword = false;
  private userData: Record<string, any> | null = null;

  // Validation rules
  private validatePassIsEqual = (rule: any, value: string, callback: Function) =>
    value !== this.form.password ? callback(new Error()) : callback();

  private rules = {
    password: [
      {
        required: true,
        message: i18n.t('validation_password'),
        trigger: 'blur',
      },
      {
        range: true,
        min: 6,
        message: i18n.t('validation_password_length', { n: 6 }),
        trigger: 'blur',
      },
    ],
    confirmPassword: [
      {
        validator: this.validatePassIsEqual,
        message: i18n.t('passwords_must_match'),
        trigger: 'blur',
      },
    ],
  };

  async created() {
    const { token } = this.$route.params;
    this.form.token = token;
    this.userData = await this.getResettingDataByToken(token);
    const resettingData = (await this.getResettingDataByToken(token))?.data;
    if (!resettingData?.mustChangePassword) {
      return;
    }

    await this.$alert(i18n.t('alert_change_password_message').toString(), i18n.t('alert_change_password_title').toString(), {
      confirmButtonText: i18n.t('alert_change_password_button').toString(),
    });
  }

  private processChangePasswordFormError() {
    this.backendError = i18n.t('error__server_validation_failure').toString();
  }

  async getResettingDataByToken(token: string) {
    try {
      this.pageIsLoading = true;
      return (await entranceHttpService.getResettingDataByToken(token)).data;
    } catch (e) {
      this.processChangePasswordFormError();
    } finally {
      this.pageIsLoading = false;
    }
  }

  async changePassword() {
    try {
      const { token, password, confirmPassword } = this.form;
      this.pageIsLoading = true;
      await entranceHttpService.changePassword({
        token,
        password,
        confirm_password: confirmPassword,
      });
      await this.$router.push({ name: 'login' });
    } catch (e: any) {
      if (e.response?.data?.errors) {
        this.applyServerValidation(e.response.data.errors);
      } else {
        this.processChangePasswordFormError();
      }
    } finally {
      this.pageIsLoading = false;
    }
  }

  applyServerValidation(errors: { [fieldName: string]: string } = {}) {
    this.backendError = Object.keys(errors).map((fieldName) => i18n.t('validation_error_' + fieldName)).join('\n');
  }

  private submitForm() {
    this.$refs.form.validate(async valid => {
      if (!valid) {
        return;
      }
      this.pageIsLoading = true;

      await this.changePassword();
    });
  }
}
</script>

<style lang="scss" scoped>
.password-recovery {
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 355px;

  &__form {
    width: 320px;
    margin: 0 auto;
  }
}
</style>
